import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '@modules/sessions/hooks/session';
import { useUser } from '@modules/users/hooks/user';

import { ToDestroy } from '@modules/users/sanitizers/user';

import { PER_PAGE, SEARCH_TYPES, SORT_TYPES } from '@shared/constants/shared';
import { BASE_URL } from '@modules/users/constants/user';

import { TUserObject } from '@modules/users/@types/TUser';
import {
  TActionFunction,
  TColumnInfo,
  TFilterProps,
} from '@shared/components/TableList';
import { TFilterByOption } from '@shared/components/TableList/Header';

import { TLoggedBranch } from '@modules/sessions/@types/TSession';

import { TableList, Container, Page } from './styles';

const columnInfos: TColumnInfo[] = [
  { key: 'id', label: 'Código' },
  { key: 'image', label: 'Imagem' },
  { key: 'name', label: 'Nome' },
  { key: 'user_key', label: 'Email' },
];

const filterByOptions: TFilterByOption[] = [
  { key: 'id', label: 'Código', type: 'normal', format: 'integer' },
  { key: 'name', label: 'Nome', type: 'normal', format: 'string' },
  { key: 'user_key', label: 'Email', type: 'normal', format: 'string' },
];

export function UserList(): JSX.Element {
  const [pageLoaded, setPageLoaded] = useState(false);

  const navigate = useNavigate();

  const { logged_branch } = useSession();
  const { list, destroy, validations, registerList, listTotal, listLoading } =
    useUser();

  function onSubmitSearch({
    filter_by,
    data,
    order,
    order_by,
    page,
    per_page,
  }: TFilterProps): void {
    const searchData = {
      order,
      order_by,
      page,
      per_page,
      for_selector: false,
      data:
        data && filter_by
          ? [
              {
                field: filter_by,
                type: SEARCH_TYPES.normal,
                values: data.split(';'),
              },
            ]
          : undefined,
    };

    list(searchData);
  }

  function addFunction() {
    navigate(`/${BASE_URL}/form`, { state: { creating: true } });
  }

  const actionFunctions = useMemo<TActionFunction<TUserObject>[]>(() => {
    return [
      {
        key: 'destroy',
        label: 'Remover',
        handle: (register: TUserObject) => {
          destroy({
            register: ToDestroy({
              register,
              logged_branch: logged_branch as TLoggedBranch,
            }),
          });
        },
        confirm_message: 'Deseja mesmo remover esse usuário?',
      },
    ];
  }, [destroy, logged_branch]);

  useEffect(() => {
    if (!registerList.length && !pageLoaded)
      list({
        order: SORT_TYPES.asc,
        order_by: 'id',
        page: 1,
        per_page: PER_PAGE,
        for_selector: false,
        data: [],
      });

    setPageLoaded(true);
  }, [list, registerList.length, pageLoaded]);

  return (
    <Page title="Usuários">
      <Container>
        <TableList
          title="Usuários"
          registerList={registerList}
          listTotal={listTotal}
          addFunction={addFunction}
          columnInfos={columnInfos}
          onSubmitSearch={onSubmitSearch}
          filterByOptions={filterByOptions}
          actionFunctions={actionFunctions}
          validations={validations}
          loading={listLoading}
        />
      </Container>
    </Page>
  );
}
